// src/firebase.js
import { initializeApp, getApp, getApps } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, enableIndexedDbPersistence } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Validate Firebase configuration
// In test/CI environments, use dummy values if not provided
if (!firebaseConfig.apiKey) {
  if (process.env.REACT_APP_CI === 'true' || process.env.NODE_ENV === 'test') {
    console.warn('[Firebase] Missing REACT_APP_FIREBASE_API_KEY in test mode, using dummy values');
    firebaseConfig.apiKey = firebaseConfig.apiKey || 'fake-api-key-for-testing';
    firebaseConfig.authDomain = firebaseConfig.authDomain || 'demo-epiphoric-test.firebaseapp.com';
    firebaseConfig.projectId = firebaseConfig.projectId || 'demo-epiphoric-test';
    firebaseConfig.storageBucket = firebaseConfig.storageBucket || 'demo-epiphoric-test.appspot.com';
    firebaseConfig.messagingSenderId = firebaseConfig.messagingSenderId || '123456789';
    firebaseConfig.appId = firebaseConfig.appId || '1:123456789:web:abcdef';
  } else {
    console.error('[Firebase] Missing REACT_APP_FIREBASE_API_KEY. Please check your environment variables.');
    throw new Error('Firebase configuration is missing. Please refresh the page or clear your browser cache.');
  }
}

let app;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  console.error('[Firebase] Initialization error:', error);
  // If app already exists, get the existing app
  if (error.code === 'app/already-initialized' || error.code === 'app/duplicate-app') {
    const apps = getApps();
    app = apps.length > 0 ? apps[0] : getApp();
  } else {
    throw error;
  }
}
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// Explicitly set auth persistence to local (default, but being explicit for mobile)
setPersistence(auth, browserLocalPersistence).catch((err) => {
  console.error('Auth persistence error:', err);
});

// Offline persistence in production only — dev/LAN QA often hits stale cached
// permission-denied after Firestore rules deploy (entitlements/summary listener).
if (process.env.NODE_ENV === 'production') {
  enableIndexedDbPersistence(db).catch((err) => {
    console.error('Firestore persistence error:', err);
  });
} else if (typeof window !== 'undefined') {
  console.info('[Firebase] Firestore project:', firebaseConfig.projectId);
}

export { auth, db, storage };